/**
 * Notification Center Service
 *
 * Read/query operations for persistent in-app notifications.
 * Calls the backend API.
 */

import type {
  AppNotification,
  NotificationCategory,
  NotificationFilters,
  NotificationPriority,
  NotificationScope,
  NotificationType,
  UnreadCounts,
} from '../types';
import { apiClient } from '@/shared/services/api';

// ---------------------------------------------------------------------------
// Backend shape
// ---------------------------------------------------------------------------

// Keys are camelCase because apiClient applies transformKeys() on all responses.
interface BackendNotification {
  id: string;
  scope: NotificationScope;
  location: string | null;
  locationName: string | null;   // location.name → locationName
  user: string | null;
  type: NotificationType;
  category: NotificationCategory;
  priority: NotificationPriority;
  title: string;
  message: string;
  isRead: boolean;               // per-caller, derived from NotificationRead
  actionUrl: string;             // action_url → actionUrl
  createdAt: string;             // created_at → createdAt
  readAt: string | null;         // read_at → readAt
  resolvedAt: string | null;     // resolved_at → resolvedAt
  resolvedByName: string | null; // resolved_by.name → resolvedByName
}

/** Helper to normalize actionUrl: empty string → undefined */
function normalizeActionUrl(url: string | null | undefined): string | undefined {
  return url && url.trim() ? url : undefined;
}

function mapBackend(b: BackendNotification): AppNotification {
  return {
    id: b.id,
    scope: b.scope,
    type: b.type,
    category: b.category,
    priority: b.priority,
    title: b.title,
    message: b.message,
    isRead: b.isRead,
    actionUrl: normalizeActionUrl(b.actionUrl),
    createdAt: new Date(b.createdAt),
    readAt: b.readAt ? new Date(b.readAt) : undefined,
    locationId: b.location ?? undefined,
    locationName: b.locationName ?? undefined,
    resolvedAt: b.resolvedAt ? new Date(b.resolvedAt) : undefined,
    resolvedByName: b.resolvedByName ?? undefined,
  };
}

// Paginated response
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Turn filters into query params.
 *
 * `locationId` is passed explicitly rather than left to whatever location is
 * active: on the backend it narrows the LOCATION scope only, so being inside
 * a location never empties the company tab or hides your own entries.
 */
function buildParams(filters?: NotificationFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters?.category && filters.category !== 'all') params.category = filters.category;
  if (filters?.type && filters.type !== 'all') params.type = filters.type;
  if (filters?.scope) {
    params.scope = Array.isArray(filters.scope) ? filters.scope.join(',') : filters.scope;
  }
  if (filters?.status && filters.status !== 'all') params.status = filters.status;
  if (filters?.locationId) params.location_id = filters.locationId;
  if (filters?.unreadOnly) params.unread = 'true';
  return params;
}

// ---------------------------------------------------------------------------
// Service functions
// ---------------------------------------------------------------------------

export async function getNotifications(filters?: NotificationFilters): Promise<AppNotification[]> {
  const response = await apiClient.get<PaginatedResponse<BackendNotification>>(
    '/api/v1/notifications/',
    { params: { ...buildParams(filters), page_size: '100' } },
  );
  return response.data.results.map(mapBackend);
}

export async function getNotificationsPaginated(
  pageParam: number = 1,
  filters?: NotificationFilters
): Promise<{ notifications: AppNotification[]; nextPage: number | null }> {
  const response = await apiClient.get<PaginatedResponse<BackendNotification>>(
    '/api/v1/notifications/',
    { params: { ...buildParams(filters), page_size: '20', page: String(pageParam) } },
  );

  return {
    notifications: response.data.results.map(mapBackend),
    nextPage: response.data.next ? pageParam + 1 : null,
  };
}

/**
 * Unread-by-you, split per scope so each bell tab carries its own count.
 *
 * Resolved entries are excluded server-side: a closed item is not waiting on
 * anyone, and counting it would leave the badge unclearable for whoever never
 * opened it before a colleague closed it.
 */
export async function getUnreadCounts(): Promise<UnreadCounts> {
  const response = await apiClient.get<UnreadCounts>(
    '/api/v1/notifications/unread_count/',
  );
  return response.data;
}

export async function markAsRead(id: string): Promise<void> {
  await apiClient.post(`/api/v1/notifications/${id}/mark_read/`);
}

export async function markUnread(id: string): Promise<void> {
  await apiClient.post(`/api/v1/notifications/${id}/mark_unread/`);
}

export async function markAllAsRead(filters?: NotificationFilters): Promise<void> {
  await apiClient.post('/api/v1/notifications/mark_all_read/', undefined, {
    params: buildParams(filters),
  });
}

/** Close a shared entry for the whole team, under your name. */
export async function resolveNotification(id: string): Promise<void> {
  await apiClient.post(`/api/v1/notifications/${id}/resolve/`);
}

/** Put a resolved entry back in the queue, for everyone. */
export async function reopenNotification(id: string): Promise<void> {
  await apiClient.post(`/api/v1/notifications/${id}/reopen/`);
}
